<template>
    <div>
        <v-row dense class="mb-4">
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('offices') }}
                </span>
            </v-col>
            <v-col cols="12" md="8">
                <div class="text-subtitle-">{{ $t('car_pick_up_note') }}:</div>
                <v-radio-group
                    v-model="contract.allOffices"
                    density="compact"
                    hide-details
                    class="pb-4"
                    @update:model-value="updateOffices"
                >
                    <v-radio :label="$t('selected_all')" :value="true" color="primary" data-cy="all-offices" />
                    <v-radio :label="$t('selected_only')" :value="false" color="primary" data-cy="selected-offices" />
                </v-radio-group>
                <template v-if="!contract.allOffices">
                    <v-data-table
                        :headers="useHeaders"
                        :items="useSelectedOffices"
                        disable-sort
                        density="compact"
                        color="primary"
                        class="elevation-1 mb-4 table-server"
                        data-cy="selected-offices-table"
                        :items-per-page="-1"
                        hide-default-footer
                    >
                        <template v-slot:item.locationType="{item}">
                            {{ $t(`locationTypes.${item.locationType}`) }}
                        </template>
                    </v-data-table>
                    <v-btn color="primary" class="mr-4" data-cy="add" @click="isShowModal = true">
                        {{ $t('add') }}
                    </v-btn>
                    <ModalWrapper v-model="isShowModal" :max-width="1200">
                        <template v-slot:title>
                            {{ $t('select_car_hire_offices') }}
                        </template>
                        <template v-slot:content>
                            <v-data-table
                                v-model="officeIds"
                                :headers="useHeaders"
                                :items="offices"
                                disable-sort
                                density="compact"
                                class="elevation-1 table-server-selected"
                                data-cy="table-pick-up-offices"
                                :items-per-page="-1"
                                hide-default-footer
                                show-select
                            >
                                <template v-slot:item.locationType="{item}">
                                    {{ $t(`locationTypes.${item.locationType}`) }}
                                </template>
                            </v-data-table>
                        </template>
                        <template v-slot:btns>
                            <v-btn data-cy="add-offices" variant="flat" @click="addOffices()">
                                {{ $t('apply') }}
                            </v-btn>
                        </template>
                    </ModalWrapper>
                </template>
            </v-col>
        </v-row>
        <v-row dense class="mb-4">
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('key_facts') }}
                </span>
            </v-col>
            <v-col cols="12" md="8">
                <v-row dense>
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="contract.vehicleCategory"
                            :items="useVehicleCategory"
                            :label="$t('category')"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="class"
                            clearable
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="contract.transmissionType"
                            :items="useTransmission"
                            :label="$t('carsRent.transmission_type')"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="class"
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="contract.vehicleClass"
                            :items="useVehicleClass"
                            :label="$t('carsRent.class')"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="class"
                            clearable
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                            v-model="contract.fuelType"
                            :items="useFuel"
                            :label="$t('carsRent.fuel_type')"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="fuel-type"
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model="contract.doorCount"
                            type="number"
                            :label="$t('carsRent.number_of_doors')"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="doors-count"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model="contract.baggageQuantity"
                            type="number"
                            :label="$t('carsRent.number_of_suitcases')"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="suitcases-count"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model="contract.passengerQuantity"
                            type="number"
                            :label="$t('carsRent.number_of_passengers')"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="passenger-quantity"
                        />
                    </v-col>
                </v-row>
                <div class="text-subtitle-">{{ $t('filterTitles.features') }}:</div>
                <v-row dense>
                    <v-col cols="12" sm="4" class="pa-0">
                        <v-checkbox
                            v-for="feature in useFirstColumnFeatures"
                            :key="feature.value"
                            v-model="contract.features"
                            :label="feature.title"
                            :value="feature.value"
                            density="compact"
                            color="primary"
                            hide-details
                            :data-cy="feature.title"
                        />
                    </v-col>
                    <v-col cols="12" sm="4" class="pa-0">
                        <v-checkbox
                            v-for="feature in useSecondColumnFeatures"
                            :key="feature.value"
                            v-model="contract.features"
                            :label="feature.title"
                            :value="feature.value"
                            density="compact"
                            color="primary"
                            hide-details
                            :data-cy="feature.title"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </div>
</template>
<script setup lang="ts">
    import {
        useFirstColumnFeatures,
        useSecondColumnFeatures,
        useTransmission,
        useVehicleCategory,
        useVehicleClass,
    } from '~/composables/form-helpers'

    const props = defineProps<{
        supplierId?: number | null
    }>()

    const offices = ref([])
    const officeIds = ref([])
    const isShowModal = ref<boolean>(false)

    const contract = defineModel<any>()

    watch(
        () => props.supplierId,
        async () => {
            if (props.supplierId) {
                await loadOffices()
            } else {
                offices.value = []
                officeIds.value = []
            }
        }
    )

    watch(isShowModal, () => {
        if (isShowModal.value) {
            officeIds.value = contract.value.officeIds
        }
    })

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.supplierId) await loadOffices()
    })

    async function loadOffices(): Promise<void> {
        try {
            const rq = {companyId: props.supplierId}
            const res = await app.$api.offices.get(rq)
            offices.value = res.offices
        } catch (e) {
            offices.value = []
        }
    }

    const useSelectedOffices = computed(() => {
        return offices.value.filter(({id}) => contract.value.officeIds.includes(id))
    })

    const useHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name'},
            {title: t('country'), value: 'countryName'},
            {title: t('city'), value: 'cityName'},
            {title: t('location_type'), value: 'locationType'},
            {title: t('address'), value: 'street'},
        ]
    })

    const addOffices = () => {
        contract.value.officeIds = officeIds.value
        isShowModal.value = false
    }

    const updateOffices = () => {
        if (contract.value.allOffices) contract.value.officeIds = []
    }
</script>
